// Hooks de dados das notificações (fatia S5b).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  NotificationPrefsResponse, NotificationPrefsUpdate, NotificationsListResponse, UnreadCountResponse,
} from '@impulso/shared/api/notifications'
import { api } from '../../lib/api'

export const notifKeys = {
  all: ['notifications'] as const,
  count: ['notifications', 'count'] as const,
  list: (unread: boolean, limit: number) => ['notifications', 'list', unread, limit] as const,
  prefs: (companyId: string) => ['notification-prefs', companyId] as const,
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notifKeys.count,
    queryFn: () => api.get<UnreadCountResponse>('/notifications/unread-count'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  })
}

export function useNotifications(opts: { unread?: boolean; limit?: number; enabled?: boolean } = {}) {
  const unread = !!opts.unread
  const limit = opts.limit ?? 30
  return useQuery({
    queryKey: notifKeys.list(unread, limit),
    queryFn: () => api.get<NotificationsListResponse>(`/notifications?limit=${limit}${unread ? '&unread=true' : ''}`),
    enabled: opts.enabled ?? true,
  })
}

export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true; unread: number }>(`/notifications/${id}/read`),
    onSuccess: (r) => {
      qc.setQueryData(notifKeys.count, { count: r.unread })
      void qc.invalidateQueries({ queryKey: notifKeys.all })
    },
  })
}

export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<{ ok: true }>('/notifications/read-all'),
    onSuccess: () => {
      qc.setQueryData(notifKeys.count, { count: 0 })
      void qc.invalidateQueries({ queryKey: notifKeys.all })
    },
  })
}

export function useNotificationPrefs(companyId: string) {
  return useQuery({
    queryKey: notifKeys.prefs(companyId),
    queryFn: () => api.get<NotificationPrefsResponse>(`/companies/${companyId}/notification-prefs`),
  })
}

export function useSaveNotificationPrefs(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NotificationPrefsUpdate) => api.put<NotificationPrefsResponse>(`/companies/${companyId}/notification-prefs`, body),
    onSuccess: (data) => qc.setQueryData(notifKeys.prefs(companyId), data),
  })
}

/** "há 5 min", "ontem", "12/09" */
export function timeAgo(iso: string, now = Date.now()): string {
  const diff = Math.max(0, now - new Date(iso).getTime())
  const min = Math.round(diff / 60_000)
  if (min < 1) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.round(h / 24)
  if (d === 1) return 'ontem'
  if (d < 7) return `há ${d} dias`
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}
